import { SubmitHandler } from "react-hook-form";
import { useNavigate } from "react-router";

import invariant from "tiny-invariant";

import { useRosterContext } from "../contexts/Roster";
import { DeletePlayerModalProps } from "../types/player";

import { alertActions } from "../../../../../features/alert/alertSlice";
import { useAppDispatch } from "../../../../../hooks/reduxHooks";
import {
    createPlayer,
    deletePlayer,
    sendWaiver,
    updatePlayer,
} from "../../../../../lib/api";
import { IPlayerFormData } from "../../../../../types/forms";

const useRosterHandlers = () => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    const { setRoster, sending, setSending, currentTeam, currentTeamsLeague } =
        useRosterContext();

    const handleWaiverClick = async () => {
        const leagueId = currentTeamsLeague?.id || 0;

        try {
            invariant(currentTeam);

            setSending(true);
            await sendWaiver(currentTeam.id, leagueId);

            dispatch(
                alertActions.success({
                    title: `Waiver sent to players`,
                    dismissible: true,
                }),
            );
        } catch (error: any) {
            let title = "";
            let text = "";

            if (/Invariant failed/i.test(error)) {
                title = "Unable to send waivers.";
                text =
                    "There was an internal error when attempting to send waivers. Please refresh the page and try again. If the problem continues, please reach out to an admin.";
            } else {
                title = "The server encountered a problem sending the waivers.";
                text =
                    "If the problem continues, please reach out to an admin.";
            }

            dispatch(
                alertActions.error({
                    title,
                    text,
                    dismissible: true,
                }),
            );
        } finally {
            setSending(false);
        }
    };

    const handleDelete = async (props: DeletePlayerModalProps) => {
        const { player, setShowDeleteModal } = props;
        setShowDeleteModal(false);

        try {
            invariant(player);
            invariant(currentTeam);

            setSending(true);
            await deletePlayer(player.id, currentTeam.id);
            setRoster(prev => prev.filter(p => p.id !== player.id));

            dispatch(
                alertActions.success({
                    title: "Player deleted successfully",
                    dismissible: true,
                    persist: true,
                }),
            );
            navigate(`/coachs-clubhouse/team/${currentTeam.id}/roster`);
        } catch (error: any) {
            dispatch(
                alertActions.error({
                    title: "The was a problem deleting the player.",
                    text: error,
                    dismissible: true,
                }),
            );
        } finally {
            setSending(false);
        }
    };

    const handleAddPlayer: SubmitHandler<IPlayerFormData> = async data => {
        try {
            invariant(currentTeam);

            setSending(true);
            const response = await createPlayer(
                data,
                currentTeam.id,
                currentTeamsLeague?.id,
            );

            setRoster(prev => {
                return [...prev, response.player];
            });

            dispatch(
                alertActions.success({
                    title: `Player created successfully`,
                    text: `${data.firstName} ${data.lastName}\n${data.email}`,
                    dismissible: true,
                }),
            );
        } catch (error: any) {
            dispatch(
                alertActions.error({
                    title: `The was a problem creating the player.`,
                    text: error,
                    dismissible: true,
                }),
            );
        } finally {
            setSending(false);
        }
    };

    const handleEditPlayer: SubmitHandler<IPlayerFormData> = async data => {
        try {
            invariant(currentTeam);
            setSending(true);
            const response = await updatePlayer(data, currentTeam.id);

            setRoster(prev => {
                return prev.map(p =>
                    p.id === data.playerId ? response.player : p,
                );
            });

            dispatch(
                alertActions.success({
                    title: `Player updated successfully`,
                    text: `${data.firstName} ${data.lastName}\n${data.email}`,
                    dismissible: true,
                }),
            );
        } catch (error: any) {
            dispatch(
                alertActions.error({
                    title: `The was a problem updating the player.`,
                    text: error,
                    dismissible: true,
                }),
            );
        } finally {
            setSending(false);
        }
    };

    return {
        handleWaiverClick,
        handleDelete,
        handleAddPlayer,
        handleEditPlayer,
        sending,
    };
};

export default useRosterHandlers;
